/**
 * AI CONTEXT
 * Purpose: toggle student task completion (complete/reset) with Firestore save.
 * Inputs: studentId — the student whose tasks array is mutated.
 * Writes: student doc tasks[] via Firebase.toggleStudentTask (transaction).
 * Used by: StudentTaskCard and front-desk task surfaces.
 */
import { App } from "antd"
import { useCallback } from "react"

import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import { type Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useErrorModal } from "#hooks/useErrorModal"
import { ErrorType } from "#utils/textFormatters"

export const useToggleStudentTask = (
  studentId: Uuid
): { toggleTask: (taskId: string) => Promise<void> } => {
  const { currentUser } = useUserContext()
  const student = useGetOneStudent(studentId)
  const openErrorModal = useErrorModal("toggling student task")
  const { message } = App.useApp()

  const toggleTask = useCallback(
    async (taskId: string) => {
      invariant(!!student, "Student doesn't exist")
      try {
        const task = (student.tasks ?? []).find(({ id }) => id === taskId)
        invariant(
          !!task,
          `Task ${taskId} is missing from _student_id ${studentId}!`
        )

        // The intent comes from the task the user clicked; the write itself
        // re-reads the array server-side so a sibling checkoff a moment
        // earlier survives.
        const isCompleted = !task.isCompleted

        await Firebase.getInstance().toggleStudentTask({
          studentId,
          taskId,
          isCompleted,
          completedBy: currentUser?.id ?? "",
        })

        message.success(
          `Task ${isCompleted ? "completed" : "reset"} for ${student.name}`
        )
      } catch (error) {
        const context = `
useToggleStudentTask
`
        openErrorModal(error as ErrorType, context)
      }
    },
    [student, currentUser, studentId, message, openErrorModal]
  )

  return { toggleTask }
}
